import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import { styles } from "./styles";
import { FileStateReverseMap, SingleProjectProps } from "./types";
import { Collaborators } from "../TracksPageComponents/Collaborators";
import { useNavigate } from "react-router-dom";
import { useContext, useState } from "react";
import {
  AuthContext,
  AuthContextInterface,
  TalkJsContextInterface,
} from "../../contexts";
import { TalkJsContext, UserDetailsContext } from "../../contexts";
import { handleCreateGroupChat } from "../TrackComponents/TrackHandler/trackHandlers";
import {
  Collaborator,
  DownloadProjectOrFolderResponse,
  FileState,
  ProjectFile,
  Role,
} from "../../proto/api_pb";
import { Project } from "../../proto/api_pb";
import { UpdateProjectResponse } from "../../proto/api_pb";
import { toast } from "react-toastify";
import { downloadProject } from "../../services/projectService";
import { updateProject } from "../../services/projectService";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import JSZip from "jszip";
import { EditProjectModal } from "../Modal/EditProjectModal";
import DeleteConfirmationModal from "../Modal/DeleteConfirmationModal";
import { APP_COLOR } from "../../theme";
import { ShareModal } from "../Modal/ShareModal";
import { ShareType } from "../../proto/api_pb";
import MenuIcon from "@mui/icons-material/Menu";
export const SingleProject: React.FC<SingleProjectProps> = ({
  project,
  session,
  setInbox,
  handleUseEffect,
  openFoldersList,
  setOpenFoldersList,
  index,
  isReorderAvailable,
  handleDragStart,
  handleDrop,
  setDraggedIndex,
}) => {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const { getValidAccessToken } = useGetValidAccessToken();
  const talkJsContext = useContext<TalkJsContextInterface>(TalkJsContext);
  const { enterprise } = useContext(UserDetailsContext);
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const [loading, setLoading] = useState<boolean>(false);

  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();

  const createGroupChat = (
    id: string | undefined,
    title: string | undefined,
    contributors: Collaborator[] | undefined
  ) => {
    handleCreateGroupChat(
      talkJsContext,
      contributors,
      navigate,
      undefined,
      id,
      title
    );
  };

  const downloadEntireProject = async () => {
    try {
      setLoading(true);
      const token = await getValidAccessToken();
      const res = await downloadProject(
        token,
        enterprise?.id as string,
        userId,
        project.getId()!
      );

      if (!(res instanceof DownloadProjectOrFolderResponse)) {
        console.error("Failed to download project", res);
        return;
      }

      const projectFiles = res.getProjectFilesList();
      const trackFiles = res.getTracksList();
      const folders = res.getFoldersList();

      if (!projectFiles.length && !trackFiles.length && !folders.length) {
        toast.warn("No files, tracks, or folders found in the project");
        return;
      }

      const zip = new JSZip();

      // ------------ Build folder map ------------
      type FolderLite = { id: string; name: string; parentId?: string };
      const folderMap: Record<string, FolderLite> = {};
      folders.forEach((f) => {
        folderMap[f.getId()] = {
          id: f.getId(),
          name: f.getName(),
          parentId: f.getParentFolderId?.() || "",
        };
      });

      // cache computed paths to avoid repeated recursion
      const folderPathCache = new Map<string, string>();

      const safeName = (s: string) =>
        (s || "")
          .replace(/[\\?%*:|"<>/]/g, "_")
          .replace(/\s+/g, " ")
          .trim();

      const getFolderPath = (folderId?: string): string => {
        if (!folderId) return "";
        if (folderPathCache.has(folderId))
          return folderPathCache.get(folderId)!;

        const seen = new Set<string>();
        const parts: string[] = [];
        let current = folderId;

        while (current && folderMap[current]) {
          if (seen.has(current)) break;
          seen.add(current);
          const f = folderMap[current];
          parts.unshift(safeName(f.name));
          current = f.parentId || "";
        }

        const p = parts.join("/");
        folderPathCache.set(folderId, p);
        return p;
      };

      // ------------ Helpers to read multiple folder IDs ------------
      const getFolderIdsFromFile = (file: any): string[] => {
        if (typeof file.getParentFolderIdList === "function") {
          return (file.getParentFolderIdList() || []).filter(Boolean);
        }
        if (typeof file.getFolderIdsList === "function") {
          return (file.getFolderIdsList() || []).filter(Boolean);
        }
        if (typeof file.getParentFolderId === "function") {
          const v = file.getParentFolderId();
          return v ? [v] : [];
        }
        if (typeof file.getFolderId === "function") {
          const v = file.getFolderId();
          return v ? [v] : [];
        }
        return [];
      };

      const getFolderIdsFromTrack = (track: any): string[] => {
        if (!track) return [];
        if (typeof track.getParentFolderIdList === "function") {
          return (track.getParentFolderIdList() || []).filter(Boolean);
        }
        if (typeof track.getFolderIdsList === "function") {
          return (track.getFolderIdsList() || []).filter(Boolean);
        }
        if (typeof track.getParentFolderId === "function") {
          const v = track.getParentFolderId();
          return v ? [v] : [];
        }
        if (typeof track.getFolderId === "function") {
          const v = track.getFolderId();
          return v ? [v] : [];
        }
        return [];
      };

      // ------------ Unique file naming helper ------------
      const usedFileNames = new Map<string, Set<string>>();

      const getUniqueFileName = (
        folderPath: string,
        baseName: string
      ): string => {
        const folderKey = folderPath || "__root__";
        if (!usedFileNames.has(folderKey)) {
          usedFileNames.set(folderKey, new Set());
        }
        const used = usedFileNames.get(folderKey)!;

        let name = baseName;
        let counter = 1;
        const extMatch = baseName.match(/(\.[^.]*)$/);
        const ext = extMatch ? extMatch[1] : "";
        const base = extMatch ? baseName.slice(0, -ext.length) : baseName;

        while (used.has(name)) {
          name = `${base} (${counter})${ext}`;
          counter++;
        }
        used.add(name);

        return name;
      };

      // ------------ Fetch cache so we only download each URL once ------------
      const blobCache = new Map<string, Promise<Blob>>();

      const fetchBlobOnce = (url: string): Promise<Blob> => {
        if (!blobCache.has(url)) {
          blobCache.set(
            url,
            (async () => {
              const resp = await fetch(url);
              if (!resp.ok) throw new Error(`Failed to fetch: ${url}`);
              return await resp.blob();
            })()
          );
        }
        return blobCache.get(url)!;
      };

      // ------------ Project files ------------
      const projectFilePromises = projectFiles.map(
        async (file: ProjectFile) => {
          const fileUrl = file.getFileUrl();
          const baseName = safeName(file.getFileName?.() || "file");

          if (!fileUrl) return;

          try {
            const blob = await fetchBlobOnce(fileUrl);

            const folderIds = getFolderIdsFromFile(file);
            const rootFolderId = `root@${project.getId()}`;
            if (folderIds.includes(rootFolderId)) {
              const uniqueName = getUniqueFileName("", baseName);
              zip.file(uniqueName, blob);
              return;
            }

            for (const fid of folderIds) {
              const fp = getFolderPath(fid);
              if (!fp) {
                continue;
              }
              const folderPath = fp || "";
              const uniqueName = getUniqueFileName(folderPath, baseName);
              const finalPath = fp ? `${fp}/${uniqueName}` : uniqueName;
              zip.file(finalPath, blob);
            }
          } catch (err) {
            console.error(`Error downloading file ${baseName}:`, err);
          }
        }
      );

      // ------------ Tracks ------------
      const trackFilePromises = trackFiles.map(async (trackResponse: any) => {
        const trackVersion = trackResponse.getLatestTrackVersion?.();
        const track = trackResponse.getTrack?.();

        if (!trackVersion) {
          console.error("Track version is undefined for track");
          return;
        }

        const trackUrl: string | undefined = trackVersion.getTrackUrl?.();
        if (!trackUrl) {
          console.error("No URL for track");
          return;
        }

        // derive extension from URL, refined by Content-Type
        const deriveExt = (
          url: string,
          contentType?: string | null
        ): string => {
          let ext = (() => {
            const parts = url.split(".");
            if (parts.length > 1) {
              return `.${parts[parts.length - 1].split("?")[0]}`;
            }
            return ".mp3";
          })();

          if (contentType) {
            const mimeToExt: Record<string, string> = {
              "audio/mpeg": ".mp3",
              "audio/wav": ".wav",
              "audio/x-wav": ".wav",
              "audio/ogg": ".ogg",
              "audio/flac": ".flac",
              "audio/aac": ".aac",
              "audio/mp4": ".m4a",
            };
            for (const [mime, e] of Object.entries(mimeToExt)) {
              if (contentType.includes(mime)) {
                ext = e;
                break;
              }
            }
          }
          return ext;
        };

        try {
          const resp = await fetch(trackUrl);
          if (!resp.ok) throw new Error(`Failed to fetch track: ${trackUrl}`);
          const trackBlob = await resp.blob();

          const contentType = resp.headers.get("content-type");
          const ext = deriveExt(trackUrl, contentType);
          const trackTitle = safeName(
            track?.getTitle?.() || "Unknown"
          ).substring(0, 50);
          const finalTrackName = `${trackTitle}${ext}`;

          const folderIds = getFolderIdsFromTrack(track);
          const rootFolderId = `root@${project.getId()}`;
          if (folderIds.includes(rootFolderId)) {
            const uniqueName = getUniqueFileName("", finalTrackName);
            zip.file(uniqueName, trackBlob);
          } else {
            for (const fid of folderIds) {
              const fp = getFolderPath(fid);
              if (!fp) {
                continue;
              }
              const folderPath = fp || "";
              const uniqueName = getUniqueFileName(folderPath, finalTrackName);
              const finalPath = fp ? `${fp}/${uniqueName}` : uniqueName;
              zip.file(finalPath, trackBlob);
            }
          }
        } catch (err) {
          console.error("Error downloading track:", err);
        }
      });

      await Promise.all([...projectFilePromises, ...trackFilePromises]);

      // ------------ Generate & download zip ------------
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${project.getName()}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Project is Ready to download!");
    } catch (error) {
      console.error("Error downloading project:", error);
      toast.error("Failed to download project.");
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async () => {
    const p = new Project();
    p.setId(project.getId());
    p.setOwnerId(project.getOwnerId());
    p.setPrivate(project.getPrivate());
    p.setName(project.getName());
    p.setDescription(project.getDescription());
    p.setArtworkUrl(project.getArtworkUrl());
    p.setProjectState(FileStateReverseMap[FileState.FILE_STATE_DELETED]);
    p.setModifiedTimestamp(Date.now());
    p.setCollaboratorsList(project.getCollaboratorsList());
    p.setTimestamp(project.getTimestamp());
    p.setEnterpriseId(enterprise?.id as string);
    const token = await getValidAccessToken();
    const res = await updateProject(token, p);
    if (res instanceof UpdateProjectResponse) {
      setIsConfirmationModalOpen(false);
      toast.info("Project deleted");
      handleUseEffect(true);
    }
  };

  return (
    <>
      {isConfirmationModalOpen && (
        <DeleteConfirmationModal
          isMessageModalOpen={isConfirmationModalOpen}
          message="Are you sure you want to delete the project?"
          toggle={() => setIsConfirmationModalOpen((prev) => !prev)}
          handleConfirm={deleteProject}
        />
      )}
      <Box
        draggable={!!isReorderAvailable}
        sx={{
          ...styles.singleProjectMainContainer,
          ...(isReorderAvailable
            ? {
                borderStyle: "dashed",
                borderColor: APP_COLOR,
                transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                boxShadow: "0 0 0 1px rgba(158, 119, 237, 0.2)",
                cursor: "grab",
              }
            : {}),
        }}
        onDragStart={(e) => {
          if (!isReorderAvailable) return;
          e.stopPropagation();
          if (handleDragStart && index !== undefined) {
            handleDragStart(index);
          }
          if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = "move";
          }
        }}
        onDragEnd={(e) => {
          if (!isReorderAvailable) return;
          e.stopPropagation();
          if (handleDrop) {
            handleDrop();
          }
        }}
        onDragOver={(e) => {
          if (isReorderAvailable) {
            e.preventDefault();
            if (setDraggedIndex && index !== undefined) {
              const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
              const offsetY = e.clientY - rect.top;
              const halfway = rect.height / 2;
              const baseIndex = index ?? 0;
              const targetIndex = offsetY > halfway ? baseIndex + 1 : baseIndex;
              setDraggedIndex(targetIndex);
            }
          }
        }}
        onDragLeave={() => {
          if (isReorderAvailable && setDraggedIndex) {
            setDraggedIndex(null);
          }
        }}
        onClick={() => {
          if (setOpenFoldersList) setOpenFoldersList([]);
          navigate(
            `/project/${encodeURIComponent(
              project.getName()
            )}/${project.getId()}`
          );
        }}
      >
        <Box sx={{ position: "relative" }}>
          <img
            style={styles.img}
            src={
              project.getArtworkUrl()?.includes("http")
                ? project.getArtworkUrl()
                : enterprise?.image || "/assets/images/logo-icon.png"
            }
            alt={`${project.getId()}`}
          />
          <Box sx={{ position: "absolute", bottom: 0, right: 0 }}>
            <Collaborators
              collaborators={project.getCollaboratorsList()}
              isGridView={true}
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px", marginTop: "5px" }}>
          {isReorderAvailable && (
            <Tooltip title="Drag to reorder projects">
              <IconButton
                sx={{
                  padding: 0,
                  margin: 0,
                  cursor: "grab",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.16)",
                    borderRadius: "4px",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    cursor: "grabbing",
                    transform: "translateY(0)",
                  },
                  transition: "background-color 0.15s ease, transform 0.15s ease",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <MenuIcon sx={{ fontSize: "18px" }} />
              </IconButton>
            </Tooltip>
          )}
          <Typography>{project.getName()}</Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              downloadEntireProject();
            }}
          >
            {loading ? (
              <CircularProgress sx={{ color: APP_COLOR }} size={25} />
            ) : (
              <DownloadIcon sx={{ fontSize: "20px" }} />
            )}
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              const contributors = project.getCollaboratorsList();
              if (session) {
                const talkInbox = session.createInbox();
                setInbox(talkInbox);
                talkJsContext.setIsChatOpen(true);
                createGroupChat(
                  project.getId(),
                  project.getName(),
                  contributors
                );
              }
              if (isSmallScreen) navigate("/messages");
            }}
          >
            <img
              style={{
                filter: "contrast(100)",
                width: "20px",
              }}
              src="/assets/icons/TracksPageIcons/message-chat-circle.svg"
              alt="icon"
            />
          </IconButton>
          <ShareModal
            id={project.getId()}
            title={project.getName()}
            shareType={ShareType.SHARE_TYPE_PROJECT}
          />
          {/* <IconButton
            onClick={(e) => {
              e.stopPropagation();
              toast.warn("Coming Soon");
            }}
          >
            <LinkIcon sx={{ fontSize: "20px" }} />
          </IconButton> */}
          {Number(enterprise?.role) !== Role.ROLE_ARTIST && (
            <EditProjectModal
              project={project}
              handleUseEffect={handleUseEffect}
            />
          )}
          {Number(enterprise?.role) !== Role.ROLE_ARTIST && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setIsConfirmationModalOpen(true);
              }}
            >
              <DeleteIcon sx={{ fontSize: "20px" }} />
            </IconButton>
          )}
        </Box>
      </Box>
    </>
  );
};
